<template>
	<div>
		<slot/>

		<slot name="marker"/>
		<template v-if="isDraggable">
			<template v-for="(latLng, latLngKey) in props.latLngs" :key="latLngKey">
				<map-marker v-if="isLatLngInBounds(latLng)" :lat-lng="latLng" :icon="icons.move" draggable
							:width="20"
							:height="20"
							@update:latLng="newLatLng => handleMovingLatLng(latLngKey, newLatLng)" @contextmenu="handleRemovedLatLng(latLngKey)"/>
			</template>
		</template>

		<div v-if="showDistanceTooltip && distanceToCursor" :style="tooltipStyle" class="z-50 fixed bg-white rounded p-4">
			{{ $n(distanceToCursor / 1000, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }} km
		</div>
	</div>
</template>

<script setup lang="ts">
import {formatLatLng} from "@spoferan/spoferan-ts-core";
import {GeolocationService} from "../../services/GeolocationService";
import {computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {mapInjection} from "../../types/keys";
import type MapMap from "./MapMap.vue";
import {moveIcon} from "../../config";
import type Ref from "vue";

const props = withDefaults(defineProps<{
	// The geolocations to create the polyline
	latLngs?: [number, number][],

	// The hex color to use as the line color
	color?: string,

	// The opacity of the polyline
	opacity?: number,

	// The thickness of the polyline
	weight?: number,

	// The zoom of the map to use as an accuracy when displaying distances
	zoom?: number,

	// States if the markers of the polyline can be moved around when clicking on it
	draggable?: boolean,

	// Whether the polyline shall receive a dashed / dotted style
	dashed?: boolean,

	// Whether the element shall retrieve a non focused styling, i.e., a decreased opacity
	unfocused?: boolean,

	// Whether a tooltip with the current distance shall be displayed when hovering over the polyline
	distanceInfo?: boolean,
}>(), {
	latLngs: [],
	color: '#008200',
	opacity: 1,
	weight: 3,
	zoom: null,
	draggable: false,
	dashed: false,
	unfocused: false,
	distanceInfo: false,
});

const emit = defineEmits(['click', 'moving']);

const map = inject<InstanceType<typeof MapMap>>(mapInjection);

let mapObject = null;

const icons = {
	move: moveIcon
}

const tooltipStyle = ref({});
const showDistanceTooltip = ref(false);
const distanceToCursor: Ref<number|null> = ref(null);
const isDraggable = ref(props.draggable);
watch(() => props.draggable, (newValue) => {
	isDraggable.value = newValue;
});

watch(() => props.latLngs, (newValue) => {
	if (mapObject) {
		mapObject.setLatLngs(newValue);
	}
}, {deep: true});

// The accuracy in meters when hovering over the polyline with the current zoom
const accuracy = computed(() => {
	const maxZoom = 18;

	if (!props.zoom) {
		return 1.1;
	}

	const zoomDiff = maxZoom - props.zoom;
	return Math.max(1.5, zoomDiff * zoomDiff * 2);
});

function addTooltipStyleListener() {
	window.addEventListener('mousemove', handleMouseMoveForTooltipStyle);
}

function removeTooltipStyleListener() {
	window.removeEventListener('mousemove', handleMouseMoveForTooltipStyle);
}

function handleMouseMoveForTooltipStyle(e) {
	let x = e.clientX,
		y = e.clientY;
	tooltipStyle.value = {
		top: (y) + 'px',
		left: (x + 20) + 'px'
	}
}

function refreshStyle() {
	if (mapObject) {
		mapObject.setText(null);
		mapObject.setText('  ►  ', {repeat: true, attributes: {fill: props.color, opacity: props.unfocused ? 0.5 : props.opacity}});

		mapObject.setStyle({
			color: props.color,
			opacity: props.unfocused ? 0.5 : props.opacity
		});
	}
}

function handleMovingLatLng(latLngIndex, latLng) {
	const newLatLngs = [...props.latLngs];
	newLatLngs[latLngIndex] = latLng
	emit('moving', {latlngs: newLatLngs})
}

function isLatLngInBounds(latLng) {
	return (!props.zoom || props.zoom > 15) && mapObject && mapObject.getBounds().contains(latLng);
}

/**
 * Gets the index of the specified coordinate on the polyline`s coordinates on which the specified coordinate should be inserted.
 *
 * @param latLng
 * @returns {number}
 */
function getLatLngInsertIndex(latLng) {
	let insertIndex = -1;
	for (let i = 1; i < props.latLngs.length; i++) {
		let bounds = L.latLngBounds(L.latLng(props.latLngs[i - 1].lat, props.latLngs[i - 1].lng), L.latLng(props.latLngs[i].lat, props.latLngs[i].lng));
		if (bounds.contains(L.latLng(latLng.lat, latLng.lng))) {
			insertIndex = i;
			break;
		}
	}

	return insertIndex;
}

function handleAddedLatLng(latLng) {
	if (!isDraggable.value) {
		return;
	}

	latLng = formatLatLng(latLng);

	// Find the position where to insert the lat lng, i.e., the index on which the lat lng is on the line between to coordinates
	const insertIndex = getLatLngInsertIndex(latLng);
	if (insertIndex > -1) {
		const newLatLngs = [...props.latLngs];
		newLatLngs.splice(insertIndex, 0, latLng)
		emit('moving', {latlngs: newLatLngs})
	}
}

function handleRemovedLatLng(latLngIndex) {
	if (!isDraggable.value) {
		return;
	}
	const newLatLngs = [...props.latLngs];
	newLatLngs.splice(latLngIndex, 1)
	emit('moving', {latlngs: newLatLngs})
}

/**
 * Calculates the distance from the specified leaflet layer point to the start of the polyline.
 */
function calculateDistanceToLatLng(latLng) {
	if (mapObject) {
		const polylineLatLngs = mapObject.getLatLngs();

		let distance = 0;
		let currentLatLng;
		for (let i = 0; i < polylineLatLngs.length - 1; i++) {
			currentLatLng = polylineLatLngs[i];

			if (GeolocationService.isPointOnPath(latLng, currentLatLng, polylineLatLngs[i + 1], accuracy.value)) {
				distance += currentLatLng.distanceTo(latLng);
				break;
			} else {
				distance += currentLatLng.distanceTo(polylineLatLngs[i + 1]);
			}
		}
		return Math.round(distance);
	}
	return null;
}

watch(() => props.opacity, () => {
	refreshStyle();
});

watch(() => props.unfocused, () => {
	refreshStyle();
});

watch(() => props.color, () => {
	refreshStyle();
});

watch(showDistanceTooltip, (newValue) => {
	if (newValue) {
		addTooltipStyleListener();
	} else {
		removeTooltipStyleListener();
	}
});

onMounted(() => {
	nextTick(async () => {
		const {polyline: leafletPolyline} = await import('leaflet/dist/leaflet-src.esm');
		await import('./polyline-text-path');

		mapObject = leafletPolyline(props.latLngs, {
			color: props.color,
			draggable: props.draggable,
			weight: props.weight,
			dashArray: props.dashed ? '5' : null,
			opacity: props.unfocused ? 0.5 : props.opacity,
			smoothFactor: 3,
		});

		// Timeout required to make sure the full path has rendered
		// Otherwise will cause an invalid array length error on client side rendering
		setTimeout(() => {
			refreshStyle();
		}, 0);

		mapObject.on('click', (event) => {
			const eventLatLng = formatLatLng(event.latlng);
			const distance = calculateDistanceToLatLng(eventLatLng);

			emit('click', {...eventLatLng, distance_from_start: distance})
		});

		mapObject.on('contextmenu', (event) => {
			handleAddedLatLng(event.latlng)
		});

		if (props.distanceInfo) {
			mapObject.on('mouseover', (event) => {
				if (props.distanceInfo) {
					showDistanceTooltip.value = true;
					distanceToCursor.value = calculateDistanceToLatLng(event.latlng);
				}
			});
			mapObject.on('mouseout', () => {
				if (props.distanceInfo) {
					showDistanceTooltip.value = false;
				}
			});
		}

		map.addElement(mapObject);
	});
});

onBeforeUnmount(() => {
	if (mapObject) {
		mapObject.remove();
	}
})

</script>